import React, { useEffect, useState, useMemo } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import './ShopInsights.css';

const toDate = (value) => {
  if (!value) return null;
  if (typeof value.toDate === 'function') return value.toDate();
  if (value instanceof Date) return value;
  return new Date(value);
};

const startOfMonth = (date = new Date()) => new Date(date.getFullYear(), date.getMonth(), 1);

const daysAgo = (days) => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d;
};

const isOnOrAfter = (date, threshold) => date && date >= threshold;

const getActivityDate = (customer) => {
  const lastVisit = toDate(customer.lastVisit);
  const lastUpdated = toDate(customer.lastUpdated);
  const createdAt = toDate(customer.createdAt);
  if (lastVisit && lastUpdated) return lastVisit > lastUpdated ? lastVisit : lastUpdated;
  return lastVisit || lastUpdated || createdAt;
};

const formatDate = (date) => {
  if (!date) return 'Onbekend';
  return date.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' });
};

const ShopInsights = ({ shopId }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [customers, setCustomers] = useState([]);
  const [posts, setPosts] = useState([]);
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    if (!shopId) return;

    const loadInsights = async () => {
      setLoading(true);
      setError('');
      try {
        const [customersSnap, postsSnap] = await Promise.all([
          getDocs(query(collection(db, 'shopCustomers'), where('shopId', '==', shopId))),
          getDocs(query(collection(db, 'feedPosts'), where('shopId', '==', shopId))),
        ]);

        const customerRows = customersSnap.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));

        const postRows = postsSnap.docs
          .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
          .filter((post) => post.status !== 'draft');

        setCustomers(customerRows);
        setPosts(postRows);
      } catch (e) {
        setError('Kon inzichten niet laden: ' + e.message);
      } finally {
        setLoading(false);
      }
    };

    loadInsights();
  }, [shopId, reloadTick]);

  const insights = useMemo(() => {
    const now = new Date();
    const monthStart = startOfMonth(now);
    const thirtyDaysAgo = daysAgo(30);

    let totalStamps = 0;
    let totalVisits = 0;
    let totalRewards = 0;
    let newCustomersThisMonth = 0;
    let activeCustomersThisMonth = 0;
    let returningCustomers = 0;
    let sleepingCustomers = 0;

    const almostReward = [];

    customers.forEach((customer) => {
      const stamps = customer.stamps || 0;
      const maxStamps = customer.maxStamps || 10;
      const visits = customer.totalVisits || 0;
      const rewards = customer.rewardsRedeemed || 0;
      const createdAt = toDate(customer.createdAt);
      const activityDate = getActivityDate(customer);

      totalStamps += stamps;
      totalVisits += visits;
      totalRewards += rewards;

      if (isOnOrAfter(createdAt, monthStart)) newCustomersThisMonth += 1;
      if (isOnOrAfter(activityDate, monthStart)) activeCustomersThisMonth += 1;

      if (visits >= 2) returningCustomers += 1;

      const isSleeping = activityDate
        ? activityDate < thirtyDaysAgo
        : createdAt && createdAt < thirtyDaysAgo;
      if (isSleeping) sleepingCustomers += 1;

      const stampsNeeded = maxStamps - stamps;
      if (stampsNeeded > 0 && stampsNeeded <= 2) {
        almostReward.push({
          id: customer.id,
          name: customer.userName || 'Onbekende klant',
          email: customer.userEmail || '',
          stamps,
          maxStamps,
          stampsNeeded,
          lastActivity: activityDate,
        });
      }
    });

    almostReward.sort((a, b) => a.stampsNeeded - b.stampsNeeded || b.stamps - a.stamps);

    const feedLikesTotal = posts.reduce((sum, post) => sum + (post.likes || 0), 0);
    const feedLikesThisMonth = posts.reduce((sum, post) => {
      const publishedAt = toDate(post.publishedAt || post.createdAt);
      if (isOnOrAfter(publishedAt, monthStart)) {
        return sum + (post.likes || 0);
      }
      return sum;
    }, 0);

    const topPosts = [...posts]
      .sort((a, b) => (b.likes || 0) - (a.likes || 0))
      .slice(0, 5);

    const avgVisits = customers.length > 0 ? (totalVisits / customers.length).toFixed(1) : '0';
    const conversionRate = customers.length > 0
      ? ((totalRewards / customers.length) * 100).toFixed(0)
      : '0';

    const tips = [];

    if (almostReward.length > 0) {
      tips.push({
        icon: '🎯',
        title: `${almostReward.length} klant${almostReward.length === 1 ? '' : 'en'} bijna bij een beloning`,
        body: 'Plaats vandaag een feed-post of spreek ze persoonlijk aan om ze terug te laten komen.',
      });
    }

    if (sleepingCustomers > 0) {
      tips.push({
        icon: '💤',
        title: `${sleepingCustomers} slapende klant${sleepingCustomers === 1 ? '' : 'en'}`,
        body: 'Deze klanten zijn 30+ dagen niet actief geweest. Overweeg een comeback-actie in je feed.',
      });
    }

    if (posts.length === 0) {
      tips.push({
        icon: '📰',
        title: 'Nog geen feed-posts',
        body: 'Deel nieuws, acties of nieuwe blends. Klanten die bij jou sparen zien jouw posts als eerste.',
      });
    } else if (feedLikesTotal === 0) {
      tips.push({
        icon: '❤️',
        title: 'Posts krijgen nog weinig likes',
        body: 'Plaats posts met een duidelijke foto en een korte actie. Bijv. “Gratis upgrade bij 8 stempels”.',
      });
    }

    if (newCustomersThisMonth > 0 && activeCustomersThisMonth < newCustomersThisMonth) {
      tips.push({
        icon: '🔄',
        title: 'Nieuwe klanten komen nog niet terug',
        body: 'Stimuleer een tweede bezoek met een welkomstbericht of kleine extra stempel bij het volgende bezoek.',
      });
    }

    if (tips.length === 0) {
      tips.push({
        icon: '✅',
        title: 'Je loyaliteitsprogramma draait goed',
        body: 'Blijf regelmatig posten en beloon terugkerende klanten om het momentum vast te houden.',
      });
    }

    return {
      monthLabel: now.toLocaleDateString('nl-NL', { month: 'long', year: 'numeric' }),
      kpis: {
        totalCustomers: customers.length,
        newCustomersThisMonth,
        activeCustomersThisMonth,
        totalStamps,
        totalRewards,
        feedLikesThisMonth,
        feedLikesTotal,
      },
      loyalty: {
        returningCustomers,
        sleepingCustomers,
        avgVisits,
        conversionRate,
      },
      almostReward,
      topPosts,
      tips: tips.slice(0, 3),
    };
  }, [customers, posts]);

  if (loading) {
    return (
      <div className="card card-flat portal-panel">
        <div className="card-body text-center">
          <div className="loading-spinner" style={{ margin: '0 auto 16px' }} />
          <p>Inzichten laden...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card card-flat portal-panel">
        <div className="card-body">
          <div className="alert alert-danger">{error}</div>
          <button type="button" className="btn btn-secondary" onClick={() => setReloadTick((t) => t + 1)}>
            Opnieuw proberen
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="insights-page">
      <div className="insights-header">
        <div>
          <div className="portal-eyebrow">Inzichten</div>
          <h2 style={{ margin: '4px 0 0' }}>Wat levert Cupdate op?</h2>
          <p className="insights-subtitle">Overzicht voor {insights.monthLabel}</p>
        </div>
        <button type="button" className="btn btn-secondary" onClick={() => setReloadTick((t) => t + 1)}>
          Vernieuwen
        </button>
      </div>

      <div className="insights-kpi-grid">
        <div className="insights-kpi">
          <span className="insights-kpi-label">Spaarders</span>
          <strong className="insights-kpi-value">{insights.kpis.totalCustomers}</strong>
          <span className="insights-kpi-meta">totaal gekoppeld</span>
        </div>
        <div className="insights-kpi">
          <span className="insights-kpi-label">Nieuw deze maand</span>
          <strong className="insights-kpi-value">{insights.kpis.newCustomersThisMonth}</strong>
          <span className="insights-kpi-meta">nieuwe klanten</span>
        </div>
        <div className="insights-kpi">
          <span className="insights-kpi-label">Actief deze maand</span>
          <strong className="insights-kpi-value">{insights.kpis.activeCustomersThisMonth}</strong>
          <span className="insights-kpi-meta">klanten met activiteit</span>
        </div>
        <div className="insights-kpi">
          <span className="insights-kpi-label">Stempels</span>
          <strong className="insights-kpi-value">{insights.kpis.totalStamps}</strong>
          <span className="insights-kpi-meta">uitgegeven in totaal</span>
        </div>
        <div className="insights-kpi">
          <span className="insights-kpi-label">Beloningen</span>
          <strong className="insights-kpi-value">{insights.kpis.totalRewards}</strong>
          <span className="insights-kpi-meta">ingewisseld</span>
        </div>
        <div className="insights-kpi">
          <span className="insights-kpi-label">Feed likes</span>
          <strong className="insights-kpi-value">{insights.kpis.feedLikesThisMonth}</strong>
          <span className="insights-kpi-meta">{insights.kpis.feedLikesTotal} totaal</span>
        </div>
      </div>

      <div className="insights-two-col">
        <div className="card card-flat">
          <div className="card-header">
            <h3>Klantloyaliteit</h3>
          </div>
          <div className="card-body insights-stat-list">
            <div className="insights-stat-row">
              <span>Terugkerende klanten</span>
              <strong>{insights.loyalty.returningCustomers}</strong>
            </div>
            <div className="insights-stat-row">
              <span>Slapende klanten (30+ dagen)</span>
              <strong>{insights.loyalty.sleepingCustomers}</strong>
            </div>
            <div className="insights-stat-row">
              <span>Gem. bezoeken per klant</span>
              <strong>{insights.loyalty.avgVisits}</strong>
            </div>
            <div className="insights-stat-row">
              <span>Beloningen per spaarder</span>
              <strong>{insights.loyalty.conversionRate}%</strong>
            </div>
          </div>
        </div>

        <div className="card card-flat">
          <div className="card-header">
            <h3>Actietips</h3>
          </div>
          <div className="card-body insights-tips">
            {insights.tips.map((tip) => (
              <div key={tip.title} className="insights-tip">
                <div className="insights-tip-icon">{tip.icon}</div>
                <div>
                  <strong>{tip.title}</strong>
                  <p>{tip.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="card card-flat">
        <div className="card-header">
          <h3>Bijna bij een beloning</h3>
        </div>
        <div className="card-body">
          {insights.almostReward.length === 0 ? (
            <p className="insights-empty">Niemand staat op dit moment op 1–2 stempels van een beloning.</p>
          ) : (
            <div className="insights-table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Klant</th>
                    <th>Voortgang</th>
                    <th>Laatste activiteit</th>
                  </tr>
                </thead>
                <tbody>
                  {insights.almostReward.map((customer) => (
                    <tr key={customer.id}>
                      <td>
                        <div className="insights-customer-name">{customer.name}</div>
                        {customer.email && <div className="insights-customer-email">{customer.email}</div>}
                      </td>
                      <td>
                        <span className="insights-progress-pill">
                          {customer.stamps}/{customer.maxStamps} · nog {customer.stampsNeeded}
                        </span>
                      </td>
                      <td>{formatDate(customer.lastActivity)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <div className="card card-flat">
        <div className="card-header">
          <h3>Beste feed-posts</h3>
        </div>
        <div className="card-body">
          {insights.topPosts.length === 0 ? (
            <p className="insights-empty">Nog geen feed-posts. Plaats je eerste post om engagement te meten.</p>
          ) : (
            <div className="insights-posts">
              {insights.topPosts.map((post) => (
                <div key={post.id} className="insights-post-card">
                  <div className="insights-post-main">
                    {post.imageUrl && (
                      <img src={post.imageUrl} alt="" className="insights-post-thumb" />
                    )}
                    <div>
                      <div className="insights-post-caption">
                        {post.caption || 'Post zonder tekst'}
                      </div>
                      <div className="insights-post-meta">
                        {formatDate(toDate(post.publishedAt || post.createdAt))}
                      </div>
                    </div>
                  </div>
                  <div className="insights-post-likes">❤️ {post.likes || 0}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShopInsights;
